/**
 * Share View Dialog
 * Allows owners to share custom views with specific users or all users
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Globe,
  Users,
  Search,
  X,
  Loader2,
  Share2,
  UserPlus,
  Check,
  Shield,
  Info
} from 'lucide-react';
import usersService from '@/services/users.service';
import { useViewShares, useUpdateViewShares } from '@/hooks/useCustomViews';
import useAuthStore from '@/stores/authStore';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface ShareViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  viewId: string | null;
  viewName: string;
}

export default function ShareViewDialog({
  open,
  onOpenChange,
  viewId,
  viewName,
}: ShareViewDialogProps) {
  const { t } = useTranslation();
  const { user: currentUser } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [shareWithAll, setShareWithAll] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  // Fetch current shares
  const { data: sharesData, isLoading: sharesLoading } = useViewShares(viewId);

  // Fetch all agents/users for selection
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['agents-for-sharing'],
    queryFn: () => usersService.getAgents(false, 'active'),
    enabled: open,
  });

  // Update shares mutation
  const updateSharesMutation = useUpdateViewShares();

  // Initialize state from current shares when data loads
  useEffect(() => {
    if (sharesData?.data?.shares) {
      const shares = sharesData.data.shares;
      const hasAllShare = shares.some((s) => s.shared_with_all);
      setShareWithAll(hasAllShare);

      const userIds = shares
        .filter((s) => s.shared_with_user_id)
        .map((s) => s.shared_with_user_id!)
        .filter((id) => id !== currentUser?.id);
      setSelectedUserIds(userIds);
    }
  }, [sharesData, currentUser?.id]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
    }
  }, [open]);

  // Filter users based on search
  const filteredUsers = useMemo(() => {
    const agents = usersData?.data?.agents || [];
    // Filter out current user
    const otherUsers = agents.filter((agent) => agent.id !== currentUser?.id);

    if (!searchQuery.trim()) {
      return otherUsers;
    }

    const query = searchQuery.toLowerCase();
    return otherUsers.filter(
      (user) =>
        user.firstName?.toLowerCase().includes(query) ||
        user.lastName?.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
    );
  }, [usersData, searchQuery, currentUser?.id]);

  // Toggle user selection
  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  // Handle save
  const handleSave = async () => {
    if (!viewId) return;

    try {
      await updateSharesMutation.mutateAsync({
        viewId,
        data: {
          shareWithAll,
          userIds: selectedUserIds,
        },
      });

      onOpenChange(false);
    } catch (error) {
      // Error is handled by the mutation's onError callback
      // Don't close the dialog on error
    }
  };

  // Get currently shared users for display
  const sharedUsers = useMemo(() => {
    return (usersData?.data?.agents || []).filter((agent) =>
      selectedUserIds.includes(agent.id)
    );
  }, [usersData, selectedUserIds]);

  const isLoading = sharesLoading || usersLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] p-0 overflow-hidden border-0 shadow-xl rounded-2xl flex flex-col h-[600px]">
        <div className="flex flex-1 overflow-hidden h-full">
          {/* Sidebar */}
          <div className="bg-slate-50 border-r border-slate-100 p-6 flex flex-col gap-4 w-[240px] flex-shrink-0">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 rounded-lg bg-red-50 flex items-center justify-center">
                  <Share2 className="h-4 w-4 text-[#0F99FD]" />
                </div>
                <span className="text-sm font-semibold text-slate-900">
                  {t('common:x1.shareView', 'Ansicht teilen')}
                </span>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">
                {t('common:x1.manageAccessFor', 'Verwalten Sie die Zugriffsrechte für "')}{viewName}".
              </p>
            </div>

            <div className="mt-auto rounded-xl bg-white p-4 border border-slate-100 shadow-sm space-y-3">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
                <Shield className="h-3.5 w-3.5 text-slate-400" />
                {t('common:field.permissions', 'Berechtigungen')}
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                {t('common:x1.sharedViewsAre', 'Geteilte Ansichten sind für andere Benutzer')} <strong>nur lesbar</strong>. Nur Sie können die Filterkriterien bearbeiten.
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="flex flex-col flex-1 bg-white min-w-0 h-full">
            <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center flex-shrink-0">
              <DialogTitle className="text-base font-semibold text-slate-900">
                {t('common:x1.shareSettings', 'Freigabe-Einstellungen')}
              </DialogTitle>
            </div>

            {isLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-[#0F99FD]" />
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Global Share Option */}
                <div className="flex items-start gap-4 p-4 rounded-xl border border-slate-100 bg-slate-50/50 hover:bg-slate-50 transition-colors">
                  <div className="h-10 w-10 rounded-full bg-white border border-slate-100 flex items-center justify-center flex-shrink-0 shadow-sm">
                    <Globe className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="share-all" className="text-sm font-semibold text-slate-900 cursor-pointer">
                        {t('common:x1.makePublic', 'Öffentlich verfügbar machen')}
                      </Label>
                      <Switch
                        id="share-all"
                        checked={shareWithAll}
                        onCheckedChange={setShareWithAll}
                        className="data-[state=checked]:bg-[#0F99FD]"
                      />
                    </div>
                    <p className="text-xs text-slate-500">
                      {t('common:x1.anyUserCanSee', 'Jeder Benutzer im System kann diese Ansicht sehen und nutzen.')}
                    </p>
                  </div>
                </div>

                {!shareWithAll && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                        <UserPlus className="h-4 w-4 text-slate-400" />
                        {t('common:x1.selectSpecificUsers', 'Bestimmte Benutzer auswählen')}
                      </Label>
                      {selectedUserIds.length > 0 && (
                        <Badge variant="secondary" className="bg-slate-100 text-slate-600 border-slate-200">
                          {selectedUserIds.length} ausgewählt
                        </Badge>
                      )}
                    </div>

                    {/* Search and List Container */}
                    <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                      <div className="p-3 border-b border-slate-100 bg-slate-50/50">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            placeholder={t('common:x1.searchByNameEmail', 'Nach Namen oder E-Mail suchen...')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 h-9 bg-white border-slate-200 focus:border-[#0F99FD] focus:ring-[#0F99FD]/20"
                          />
                        </div>
                      </div>

                      <ScrollArea className="h-[240px] bg-white">
                        <div className="p-2 space-y-1">
                          {filteredUsers.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-10 text-center">
                              <div className="h-10 w-10 rounded-full bg-slate-50 flex items-center justify-center mb-2">
                                <Users className="h-5 w-5 text-slate-300" />
                              </div>
                              <p className="text-sm font-medium text-slate-900">{t('common:state.noUsers', 'Keine Benutzer gefunden')}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                {searchQuery ? 'Versuchen Sie einen anderen Suchbegriff' : 'Es sind keine weiteren Benutzer verfügbar'}
                              </p>
                            </div>
                          ) : (
                            filteredUsers.map((user) => {
                              const isSelected = selectedUserIds.includes(user.id);
                              return (
                                <div
                                  key={user.id}
                                  onClick={() => toggleUser(user.id)}
                                  className={cn(
                                    'group flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-all border border-transparent',
                                    isSelected
                                      ? 'bg-red-50 border-red-100'
                                      : 'hover:bg-slate-50 hover:border-slate-100'
                                  )}
                                >
                                  <div className={cn(
                                    "flex h-5 w-5 items-center justify-center rounded border transition-colors",
                                    isSelected
                                      ? "bg-[#0F99FD] border-[#0F99FD] text-white"
                                      : "border-slate-300 bg-white group-hover:border-slate-400"
                                  )}>
                                    {isSelected && <Check className="h-3.5 w-3.5" />}
                                  </div>
                                  
                                  <Avatar className="h-8 w-8 border border-slate-100">
                                    <AvatarImage
                                      src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${user.email}`}
                                    />
                                    <AvatarFallback className="text-xs bg-slate-100 text-slate-600">
                                      {user.firstName?.charAt(0)}
                                      {user.lastName?.charAt(0)}
                                    </AvatarFallback>
                                  </Avatar>
                                  
                                  <div className="flex-1 min-w-0">
                                    <p className={cn(
                                      "text-sm font-medium truncate",
                                      isSelected ? "text-[#0F99FD]" : "text-slate-700"
                                    )}>
                                      {user.firstName} {user.lastName}
                                    </p>
                                    <p className="text-xs text-slate-400 truncate">
                                      {user.email}
                                    </p>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </ScrollArea>
                    </div>

                    {/* Selected Users Chips */}
                    {sharedUsers.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-2">
                        {sharedUsers.map((user) => (
                          <Badge
                            key={user.id}
                            variant="secondary"
                            className="bg-white border border-slate-200 text-slate-600 pl-1 pr-2 py-1 h-7 hover:bg-slate-50 transition-colors"
                          >
                            <Avatar className="h-4 w-4 mr-1.5">
                              <AvatarFallback className="text-[9px] bg-slate-100 text-slate-500">
                                {user.firstName?.charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-xs font-medium mr-1.5">
                              {user.firstName} {user.lastName}
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleUser(user.id);
                              }}
                              className="ml-auto rounded-full p-0.5 hover:bg-slate-200 text-slate-400 hover:text-slate-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Info className="h-3.5 w-3.5" />
                {shareWithAll ? (
                  <span>{t('common:x1.visibleTo', 'Sichtbar für')} <strong>alle Benutzer</strong></span>
                ) : (
                  <span>{t('common:x1.visibleTo', 'Sichtbar für')} <strong>{selectedUserIds.length} Benutzer</strong></span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="h-9 rounded-lg border-slate-200 text-slate-600 hover:text-slate-900 hover:bg-white"
                >
                  {t('common:action.cancel', 'Abbrechen')}
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={updateSharesMutation.isPending}
                  className="h-9 rounded-lg bg-[#0F99FD] text-white hover:bg-[#0c84e0] shadow-sm"
                >
                  {updateSharesMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('common:x1.savingDots', 'Speichern...')}
                    </>
                  ) : (
                    'Speichern'
                  )}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
